import { alarmsDTO } from "../../components/UserAlarms/tenantApiCalls";
import { MisEntry, MisModel } from "../../interfaces/ActionStartResponse";
import { iActiveSlaCycle, iCommunication, iEnquiry } from "../../interfaces/iEnquiry";
import { iUser } from "../../interfaces/iUser";
import { getLastTouched } from "../LastTouched/misc";
import { CallEmsApi, iEmsApiResponse, iEmsApiResponseBody } from "./FetchExtension";

export async function CreateEnquiry(mailbox: number): Promise<iEmsApiResponseBody<iEnquiry>> {
    return await CallEmsApi<iEnquiry>("POST", "Enquiries", { MailboxID: mailbox });
}

export async function GetMyEnquiries(
    getCompleted: boolean = true,
    dateCompleted?: string,
): Promise<iEmsApiResponseBody<{ enquiries: iEnquiry[] }>> {
    return await CallEmsApi<{ enquiries: iEnquiry[] }>(
        "GET",
        "Enquiries/My" + (getCompleted ? "?getCompleted=true&dateCompleted=" + (dateCompleted ?? "") : ""),
    );
}

export async function GetQueueEnquiries(
    queue: number,
): Promise<iEmsApiResponseBody<{ enquiries: iEnquiry[]; totalEnquiries: number }>> {
    const lastTouched = getLastTouched();
    const headers = new Headers();
    headers.append("pageNumber", lastTouched.Queue.Pagination.pageNumber.toString());
    headers.append("sortColumn", lastTouched.Queue.Pagination.sortColumn);
    headers.append("sortAscending", lastTouched.Queue.Pagination.sortAscending.toString());
    headers.append(
        "itemsPerpage",
        process.env.REACT_APP_ITEMS_PER_PAGE ? process.env.REACT_APP_ITEMS_PER_PAGE : "1000",
    );
    return await CallEmsApi<{ enquiries: iEnquiry[]; totalEnquiries: number }>(
        "GET",
        `Enquiries/Queue/${queue}`,
        undefined,
        headers,
    );
}

export interface AdvancedSearchRequest {
    EnquiryReference?: string | undefined;
    OtherReference?: string | undefined;
    OtherReferenceClause: SearchClause;
    EnquirerName?: string | undefined;
    EnquirerNameClause: SearchClause;
    EmailAddress?: string | undefined;
    EmailAddressClause: SearchClause;
    Subject?: string | undefined;
    SubjectClause: SearchClause;
    Body?: string | undefined;
    BodyClause: SearchClause;
    Owner?: number | undefined;
    EnquiryStatuses?: Array<number> | undefined;
    Mailboxes?: Array<number> | undefined;
    ReceivedAfter?: string | undefined;
    ReceivedBefore?: string | undefined;
    ReturnFirst: number;
}
export enum SearchClause {
    StartsWith = 1,
    Contains = 2,
}
export async function GetAdvancedSearchResults(
    request: AdvancedSearchRequest,
): Promise<iEmsApiResponseBody<{ enquiries: iEnquiry[] }>> {
    return await CallEmsApi<{ enquiries: iEnquiry[] }>("POST", "Enquiries/AdvancedSearch", request);
}

export interface AllocateEnquiriesRequest {
    UserID: number | undefined;
    Enquiries: Array<number>;
}
export async function AllocateEnquiries(request: AllocateEnquiriesRequest): Promise<iEmsApiResponse> {
    return await CallEmsApi<any>("POST", "Enquiries/Allocate", request);
}

export interface UpdateEnquiriesstatusRequest {
    StatusID: number | undefined;
    Enquiries: Array<number>;
}
export async function UpdateEnquiriesStatus(request: UpdateEnquiriesstatusRequest): Promise<iEmsApiResponse> {
    return await CallEmsApi<any>("POST", "Enquiries/UpdateStatus", request);
}

export interface PrioritiseEnquiriesRequest {
    Priority: number | undefined;
    Enquiries: Array<number>;
}
export async function PrioritiseEnquiries(request: PrioritiseEnquiriesRequest): Promise<iEmsApiResponse> {
    return await CallEmsApi<any>("POST", "Enquiries/Prioritise", request);
}

export interface RemoveEnquiriesRequest {
    Enquiries: Array<number>;
}
export async function RemoveEnquiries(request: RemoveEnquiriesRequest): Promise<iEmsApiResponse> {
    return await CallEmsApi<any>("POST", "Enquiries/Remove", request);
}
export interface NoActionRequiredEnquiryRequest {
    Enquiries: Array<number>;
}
export async function NoActionRequiredEnquiries(request: NoActionRequiredEnquiryRequest): Promise<iEmsApiResponse> {
    return await CallEmsApi<any>("POST", "Enquiries/NoActionRequired", request);
}

export async function TakeOwnership(enquiries: Array<number>): Promise<iEmsApiResponse> {
    return await CallEmsApi<any>("POST", "Enquiries/TakeOwnership", { Enquiries: enquiries });
}

export interface iEnquiryDetails {
    id: number;
    mailbox: number;
    enquiryReference: string;
    enquirerName: string;
    enquirerEmail: string;
    priority: number;
    subject: string;
    status: number;
    workItems: number;
    updatedBy: iUser;
    updatedAt: string;
    sentAt: string;
    receivedAt: string;
    allocatedAt: string;
    completedAt: string;
    boxSlaCycle: iActiveSlaCycle;
    responseSlaCycle: iActiveSlaCycle;
    owners: iEnquiryOwner[];
}
export interface iEnquiryOwner {
    user: iUser;
    assignedAt?: string;
    assignedBy?: iUser;
}
export interface AlarmProperties extends alarmsDTO {
    user: iUser;
}
export interface LogEntry {
    dateTime: string;
    statusName?: string;
    updatedBy?: iUser;
}
export interface SlaProductivity {
    user?: iUser;
    entryStatusName?: string;
    exitStatusName?: string;
    workItems?: number;
    hasInbound?: boolean;
    hasOutbound?: boolean;
    timeAchieved?: number;
    wizardEntry?: string;
    wizardExit?: string;
}
export interface SlaUser {
    user?: iUser;
    dateStarted: string;
    dateCompleted?: string;
    timeAchieved?: number;
    slaTimeout?: string;
    statusName?: string;
}
export interface SlaResponse {
    user?: iUser;
    responseStart?: string;
    responseStop: string;
    responseAchieved?: number;
}
export interface iEnquiryProperties extends iEnquiryDetails {
    alarms: AlarmProperties[];
    logEntries: LogEntry[];
    slaProductivity: SlaProductivity[];
    slaUsers: SlaUser[];
    slaResponses: SlaResponse[];
    misModel: MisModel;
    misData: MisEntry[];
}

export async function GetEnquiryDetails(enquiryId: number): Promise<iEmsApiResponseBody<iEnquiryDetails>> {
    return await CallEmsApi<iEnquiryDetails>("GET", `Enquiries/${enquiryId}`);
}

export async function GetEnquiryProperties(enquiryId: number): Promise<iEmsApiResponseBody<iEnquiryProperties>> {
    return await CallEmsApi<iEnquiryProperties>("GET", `Enquiries/${enquiryId}/Properties`);
}

export async function UpdateEnquiryDetails(iEnquiryDetails: iEnquiryDetails): Promise<iEmsApiResponse> {
    return await CallEmsApi<iEnquiryDetails>("PUT", `Enquiries/${iEnquiryDetails.id}`, iEnquiryDetails);
}

export async function GetEnquiryCommunications(
    enquiryId: number,
    limit?: number,
): Promise<iEmsApiResponseBody<iCommunication[]>> {
    const result = await CallEmsApi<iCommunication[]>(
        "GET",
        `Enquiries/${enquiryId}/Communications${limit ? `?limit=${limit}` : ""}`,
    );
    if (result.isOk) {
        result.data = result.data?.sort((a: iCommunication, b: iCommunication) => {
            if (b.id > a.id) {
                // fallback on id/which record was inserted last
                return 1;
            } else if (b.id < a.id) {
                return -1;
            } else {
                return 0;
            }
        });
    }
    return result;
}

export interface iCommCount {
    count: number;
    isPendingProcessing: boolean;
}

export async function GetEnquiryCommunicationCount(enquiryId: number): Promise<iEmsApiResponseBody<iCommCount>> {
    return await CallEmsApi<iCommCount>("GET", `Enquiries/${enquiryId}/Communications/Count`);
}

export async function StartUnArchiveProcess(enquiryId: number): Promise<iEmsApiResponseBody<boolean>> {
    return await CallEmsApi<boolean>("POST", `Enquiries/${enquiryId}/Communications/UnArchive`);
}

// MyTeams calls
export async function GetMyTeamMemberEnquiries(
    userId: number,
    getCompleted: boolean = true,
    dateCompleted?: string,
): Promise<iEmsApiResponseBody<{ enquiries: iEnquiry[] }>> {
    return await CallEmsApi<{ enquiries: iEnquiry[] }>(
        "GET",
        `MyTeam/Enquiries/${userId}/${getCompleted ? "?getCompleted=true&dateCompleted=" + (dateCompleted ?? "") : ""}`,
    );
}

export async function GetMyTeamMembers(): Promise<iEmsApiResponseBody<{ users: { id: number; name: string }[] }>> {
    return await CallEmsApi<{ users: { id: number; name: string }[] }>("GET", `MyTeam/Members/`);
}
